import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from '../context/ConsentContext';
import { useSettings } from '../context/SettingsContext';

/**
 * Injects Google Tag (GTAG) + Meta / Facebook Pixel scripts based on the
 * IDs in Site Settings AND the user's consent choice.
 *
 * - GTAG loads only when `consent.analytics === true` AND `settings.gtag_id`
 * - Meta Pixel loads only when `consent.marketing === true` AND `settings.meta_pixel_id`
 * - Once injected, we don't re-inject on every navigation; we DO fire a
 *   pageview event on each route change so SPA transitions are tracked.
 * - Removing consent (via `<Manage cookies>` link) will prevent NEW pageviews
 *   from firing, but past scripts remain in memory until page reload —
 *   this matches every major CMP's behaviour.
 */
const loadOnce = (id, factory) => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const el = factory();
  el.id = id;
  document.head.appendChild(el);
};

/**
 * Redact sensitive query params (token=, session_id=) from a query string
 * before forwarding to third-party analytics. Prevents password-reset,
 * email-verification, and unsubscribe tokens leaking into GA / Meta logs.
 */
const REDACTED_PARAMS = ['token', 'session_id'];
const redactSearch = (search) => {
  if (!search) return '';
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    let dirty = false;
    for (const key of REDACTED_PARAMS) {
      if (params.has(key)) {
        params.set(key, 'REDACTED');
        dirty = true;
      }
    }
    if (!dirty) return search.startsWith('?') ? search : `?${search}`;
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  } catch {
    return '';
  }
};

const AnalyticsTags = () => {
  const { analytics, marketing } = useConsent() || {};
  const { settings } = useSettings() || {};
  const location = useLocation();
  const gtagId = (settings?.gtag_id || '').trim();
  const pixelId = (settings?.meta_pixel_id || '').trim();
  const injected = useRef({ gtag: false, pixel: false });

  // ---- GTAG injection ----
  useEffect(() => {
    if (!analytics || !gtagId || injected.current.gtag) return;
    // Loader
    loadOnce('dt-gtag-loader', () => {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gtagId)}`;
      return s;
    });
    // Config
    loadOnce('dt-gtag-config', () => {
      const s = document.createElement('script');
      s.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${gtagId}',{'anonymize_ip':true,'send_page_view':false});`;
      return s;
    });
    injected.current.gtag = true;
  }, [analytics, gtagId]);

  // ---- Meta Pixel injection ----
  useEffect(() => {
    if (!marketing || !pixelId || injected.current.pixel) return;
    loadOnce('dt-meta-pixel', () => {
      const s = document.createElement('script');
      s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
      return s;
    });
    injected.current.pixel = true;
  }, [marketing, pixelId]);

  // ---- SPA route-change pageview ----
  useEffect(() => {
    // Redact tokens from the tracked URL. Also builds a clean page_location
    // by stripping the same params off window.location.href before sending.
    const safeSearch = redactSearch(location.search);
    const pagePath = location.pathname + safeSearch;
    let pageLocation = '';
    if (typeof window !== 'undefined') {
      try {
        const u = new URL(window.location.href);
        for (const key of REDACTED_PARAMS) {
          if (u.searchParams.has(key)) u.searchParams.set(key, 'REDACTED');
        }
        pageLocation = u.toString();
      } catch {
        pageLocation = window.location.origin + pagePath;
      }
    }
    if (analytics && gtagId && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_location: pageLocation,
        page_title: document.title,
      });
    }
    if (marketing && pixelId && typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, location.search, analytics, marketing, gtagId, pixelId]);

  return null;
};

export default AnalyticsTags;
